import api from './axiosConfig';

interface UploadImageResponse {
  url: string;
}

interface UploadMediaResponse {
  url: string;
  type: string; // 'IMAGE' | 'VIDEO'
}

/** Recebe 0..100 conforme os bytes saem daqui. */
export type OnProgresso = (pct: number) => void;

/**
 * Upload de arquivos via backend (/api/v1/upload). O backend encaminha ao
 * Supabase Storage com a service-role key e devolve a URL pública.
 */
// O cliente axios tem Content-Type: application/json por padrão, o que faria o
// axios serializar o FormData como JSON. Forçar multipart/form-data faz o
// browser definir o header com o boundary correto.
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

/**
 * Config do axios com medição de progresso.
 *
 * ponytail: o número mede só o que sai daqui para o backend. O repasse do
 * backend para o Supabase acontece depois, e não há como enxergá-lo — por isso
 * a tela troca para "Enviando…" ao chegar em 100% em vez de dizer que acabou.
 * Sem `total` (resposta sem Content-Length), nada é reportado.
 */
const comProgresso = (onProgresso?: OnProgresso) => ({
  ...MULTIPART,
  onUploadProgress: onProgresso
    ? (e: { loaded: number; total?: number }) => {
        if (e.total) onProgresso(Math.round((e.loaded * 100) / e.total));
      }
    : undefined,
});

const corpo = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return form;
};

const FileStorageService = {
  /**
   * POST /api/v1/upload/image → { url }
   * Body: multipart/form-data com o campo `file`.
   */
  uploadImage: (file: File, onProgresso?: OnProgresso): Promise<UploadImageResponse> =>
    api
      .post<UploadImageResponse>('/upload/image', corpo(file), comProgresso(onProgresso))
      .then((r) => r.data),

  /** POST /api/v1/upload/product-image → { url } — foto de produto da prateleira (bucket my-products). */
  uploadProductImage: (file: File, onProgresso?: OnProgresso): Promise<UploadImageResponse> =>
    api
      .post<UploadImageResponse>('/upload/product-image', corpo(file), comProgresso(onProgresso))
      .then((r) => r.data),

  /**
   * POST /api/v1/upload/media → { url, type }
   * Body: multipart/form-data com o campo `file`.
   */
  uploadMedia: (file: File, onProgresso?: OnProgresso): Promise<UploadMediaResponse> =>
    api
      .post<UploadMediaResponse>('/upload/media', corpo(file), comProgresso(onProgresso))
      .then((r) => r.data),
};

export default FileStorageService;
